import { endPoints } from "const/endPoints";
import { fetchWithToken } from "helpers/fetch";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmQuiz } from "../ConfirmQuiz";
import { SectionContainer } from "../Lessons/styles";
import { Question } from "./Question";
import { Box } from "@mui/system";
import { Typography } from "@mui/material";
import { Timer } from "./Timer";
import { useSelector } from "react-redux";
import { QuizResults } from "./Modal/QuizResults";
import Spinner from "components/common/Spinner";
import Toast from "components/common/Popup/Toast";

export const Quiz = () => {
  const navigate = useNavigate();
  const courses = useSelector((state) => state.courses.courses);
  const { lessonId, courseTitle } = useParams();

  const [confirm, setConfirm] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [loading, setLoading] = useState(true);

  const [quizResults, setQuizResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [displayModal, setDisplayModal] = useState(false);
  const [responses, setResponses] = useState([]);

  //**** Modal */
  const onDislayModal = () => {
    setDisplayModal(true);
  };
  const courseCleanTitle = courseTitle.replace(/-/g, " ");
  const actualCourse =
    courses &&
    courses?.find((course) => {
      return course.title === courseCleanTitle;
    });
  const getQuizContent = async () => {
    try {
      setLoading(true);

      const quizResponse = await fetchWithToken(
        `${endPoints.get_quiz_by_id}/${lessonId}`
      );

      if (quizResponse.status !== 200) {
        console.error("Error fetching quiz:", quizResponse.status);
        return;
      }

      const quizData = await quizResponse.json();

      const questionsResponse = await fetchWithToken(
        `${endPoints.get_all_questions_by_quiz}/${lessonId}`
      );

      if (questionsResponse.status !== 200) {
        console.error("Error fetching questions:", questionsResponse.status);
        return;
      }

      const questionsData = await questionsResponse.json();

      console.log("Quiz data:", quizData);
      console.log("Questions data:", questionsData);

      setQuiz(quizData);
      setQuestions(questionsData);

      // Only initialize responses if we have valid questions data
      if (Array.isArray(questionsData) && questionsData.length > 0) {
        const initialResponses = questionsData.map((question) => ({
          questionId: question.id,
          option: {
            title: "",
            isCorrect: false,
          },
        }));
        setResponses(initialResponses);
      } else {
        console.log("No questions found for quiz");
        setResponses([]);
      }
    } catch (error) {
      console.error("Error fetching quiz content:", error);
    } finally {
      setLoading(false);
    }
  };

  const setScore = (responses, courseId, quizId) => {
    // Se envian questionId y optionId para que el servidor empareje cada
    // respuesta con su pregunta y determine el acierto por si mismo.
    const options = responses.map((response) => ({
      questionId: response.questionId,
      optionId: response.option.id,
      title: response.option.title,
    }));
    const scoreFormat = {
      courseId,
      quizId,
      options,
    };
    return scoreFormat;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Verificar que el curso existe en Redux, si no, buscarlo por título
      let courseId = actualCourse?.id;
      if (!courseId) {
        try {
          const coursesResp = await fetchWithToken(endPoints.get_all_courses);
          if (coursesResp.ok) {
            const allCourses = await coursesResp.json();
            const found = allCourses.find(
              (c) => c.title === courseCleanTitle
            );
            courseId = found?.id;
          }
        } catch (e) {
          console.error("Error fetching courses:", e);
        }
      }

      if (!courseId) {
        Toast("error", "No se pudo encontrar el curso. Intenta recargar la página.");
        return;
      }

      if (!quiz) {
        Toast("error", "No se pudo cargar el quiz. Intenta recargar la página.");
        return;
      }

      // Verificar que todas las preguntas estén respondidas
      const unanswered = responses.filter((r) => !r.option.title);
      if (unanswered.length > 0) {
        Toast("error", `Debes responder todas las preguntas. Faltan ${unanswered.length}.`);
        return;
      }

      const score = setScore(responses, courseId, quiz.id);
      const response = await fetchWithToken(endPoints.set_score, score, "PUT");

      if (response.status === 200) {
        const data = await response.json();
        setQuizResults(data);
        onDislayModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const msg = errorData.message || "Error al enviar el quiz";
        Toast("error", typeof msg === "string" ? msg : msg[0] || "Error al enviar el quiz");
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
      Toast("error", "Error de conexión. Verifica tu internet e intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    getQuizContent();
  }, []);

  useEffect(() => {
    // Only log responses for debugging, don't call setScore on every response change
    if (quiz && questions && responses.length > 0) {
      console.log("Current responses:", responses);
    }
  }, [responses, quiz, questions]);

  if (loading) {
    return (
      <SectionContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spinner />
        </Box>
      </SectionContainer>
    );
  }

  if (!quiz || !questions) {
    return (
      <SectionContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Typography>Error loading quiz. Please try again.</Typography>
        </Box>
      </SectionContainer>
    );
  }

  return (
    <>
      <SectionContainer>
        {!confirm ? (
          <ConfirmQuiz quiz={quiz} setConfirm={setConfirm} />
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Timer
              duration={quiz.duration}
              handleSubmit={handleSubmit}
              onDislayModal={onDislayModal}
            />
            <Box
              sx={{
                width: { xs: "95%", sm: "80%", md: "50%" },
                marginTop: "2rem",
              }}
            >
              {Array.isArray(questions) && questions.length > 0 ? (
                questions.map((question) => (
                  <Question
                    key={question.id}
                    responses={responses}
                    setResponses={setResponses}
                    question={question}
                  />
                ))
              ) : (
                <Typography>No questions available for this quiz.</Typography>
              )}
            </Box>
            <QuizResults
              handleSubmit={handleSubmit}
              submitting={submitting}
              displayModal={displayModal}
              quizResults={quizResults}
              onClose={() => {
                setDisplayModal(false);
                // Navegar de regreso al curso después de ver resultados
                navigate(-1);
              }}
            />
          </Box>
        )}
      </SectionContainer>
    </>
  );
};
